"use client";
import Image, { StaticImageData } from "next/image";
import { useTranslations } from "next-intl";
import { useState, useRef, memo } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import clsx from "clsx";

import Logo from "@/shared/components/Logo";
import AppStoreButton from "@/shared/components/AppStoreButton";
import Rating from "@/shared/components/Rating";
import { Controller } from "@/shared/components/Slider";
import SectionLink from "@/shared/components/SectionLink";
import { PATHS } from "@/shared/utils/paths";

import phone1 from "@public/iphone1.png";
import phone2 from "@public/iphone2.png";
import phone3 from "@public/iphone3.png";
import phone4 from "@public/iphone4.png";
import phone5 from "@public/iphone5.png";

import s from "./styles.module.css";

interface Item {
  img: StaticImageData;
  i18nkey: string;
}

const ELEMENTS: Item[] = [
  { img: phone1, i18nkey: "text1" },
  { img: phone2, i18nkey: "text2" },
  { img: phone3, i18nkey: "text3" },
  { img: phone4, i18nkey: "text4" },
  { img: phone5, i18nkey: "text5" },
];

const interpolateProgress = (
  start: number,
  end: number,
  current: number,
  outputStart: number,
  outputEnd: number
): number => {
  const normalizedProgress = (current - start) / (end - start);
  const result = outputStart + (outputEnd - outputStart) * normalizedProgress;
  return result;
};

const MIN_SCALE = 0.8;
const MAX_SCALE = 1;

const MOBILE_SLIDE_WIDTH = 220;
// const DESKTOP_SLIDE_WIDTH = 400; / TODO
const STEP_PROGRESS = 1 / (ELEMENTS.length - 1);

const SNAPS: number[] = ELEMENTS.reduce(
  (acc: number[], _, i) => [...acc, i * STEP_PROGRESS],
  []
);

const getTransform = (
  start: number,
  end: number,
  progress: number
): { scale: number; opacity: number } => {
  const scale = interpolateProgress(start, end, progress, MIN_SCALE, MAX_SCALE);
  const opacity =
    progress <= end
      ? // straight
        interpolateProgress(start, end, progress, 0.3, 1)
      : // backwards ;
        interpolateProgress(end, end + STEP_PROGRESS, progress, 1, 0);

  return { scale: Math.min(scale, MAX_SCALE), opacity };
};

interface WrapperImageProps {
  src: StaticImageData;
  progress: number;
  index: number;
}

const WrapperImage = memo(function Img({
  src,
  progress,
  index,
}: WrapperImageProps) {
  const divRef = useRef<HTMLDivElement>(null);

  const start = SNAPS[index] - STEP_PROGRESS;
  const end = SNAPS[index];

  const { scale, opacity } = getTransform(start, end, progress);

  return (
    <div
      ref={divRef}
      className={s.iphone}
      style={{ transform: `scale(${scale})`, opacity }}
    >
      <Image
        src={src}
        alt=""
        className={s.phoneImg}
        sizes="(min-width: 768px) 220px, 55vw"
      />
    </div>
  );
});

gsap.registerPlugin(useGSAP, ScrollTrigger);

const getCurrentStep = (progress: number) => {
  const totalSteps = ELEMENTS.length;
  const inBetweenSteps = STEP_PROGRESS / 2;

  return Math.max(
    0,
    Math.min(
      totalSteps - 1,
      Math.floor((progress + inBetweenSteps) / STEP_PROGRESS)
    )
  );
};

export default function PhonesCarousel() {
  const t = useTranslations("PhonesCarousel");
  const [progress, setProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const slideWidth = MOBILE_SLIDE_WIDTH;

      if (!sliderRef.current || !containerRef.current) {
        return;
      }
      const topOffset = -90;
      const contentWidth = slideWidth * ELEMENTS.length - 1;

      // keep last slide on the screen
      const adjustedX = -(contentWidth - slideWidth);

      gsap.set(sliderRef.current, {
        width: `${contentWidth}px`,
        x: 0,
      });

      const animation = gsap.to(sliderRef.current, {
        x: adjustedX,
        ease: "none",
      });

      const scrollTrigger = ScrollTrigger.create({
        animation: animation,
        trigger: containerRef.current,
        start: `top top-=${topOffset}px`,
        end: `+=${contentWidth * 2}`,
        snap: {
          snapTo: SNAPS,
          duration: 1,
          delay: 0.001,
          ease: "expo.out",
        },
        pin: true,
        scrub: 1,
        onUpdate: (self) => setProgress(self.progress),
        invalidateOnRefresh: true,
        // markers: true,
      });

      return () => {
        scrollTrigger.kill();
        animation.kill();
        gsap.set(sliderRef.current, { clearProps: "all" });
      };
    },
    { scope: containerRef }
  );

  return (
    <div ref={containerRef} className={s.wrapper}>
      <section className={s.section}>
        <div className={s.heading}>
          <h2 className={s.title}>{t("title")}</h2>
          <Rating />
        </div>
        <div className={s.container}>
          <div ref={sliderRef} className={s.slider}>
            {ELEMENTS.map((item, i) => {
              return (
                <div
                  key={`phone_${i}`}
                  className={s.slide}
                  style={{ width: MOBILE_SLIDE_WIDTH + "px" }}
                >
                  <WrapperImage progress={progress} src={item.img} index={i} />
                </div>
              );
            })}
          </div>
          <div className={s.controller}>
            <Controller total={1} current={progress} />
          </div>
        </div>
        <div className={s.info}>
          <Logo type="square" className={s.logoDesktop} />
          <div className={s.textContainer}>
            {ELEMENTS.map((item, i) => (
              <p
                key={"phone" + item.i18nkey + i}
                className={clsx(
                  s.text,
                  getCurrentStep(progress) === i && s.visible
                )}
              >
                {t.rich(item.i18nkey, {
                  tinted: (text) => <span className={s.tinted}>{text}</span>,
                })}
              </p>
            ))}
          </div>
          <div className={s.footer}>
            <Logo type="square" className={s.logoMobile} />
            <AppStoreButton />
          </div>
          <SectionLink href={PATHS.wallet}>{t("walletLink")}</SectionLink>
        </div>
      </section>
    </div>
  );
}
